import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import SpeechRecognition from 'react-speech-recognition';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ActiveChatSessionContext, SubmitMessageContext, ChatSessionInputContext } from '@trinity/chat-components/components/Chat/context';
import { useSpeechRecognition } from '@trinity/chat-components/components/Chat/hooks/useSpeechRecognition';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { TextToSpeechStateContext, StopTextToSpeechContext, VoiceModeContext, LoadingVoiceClipContext, MicrophoneStateContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { capitalize } from '@trinity/chat-components/utils';
import { requestMicrophonePermissionIfNeeded } from '@trinity/chat-components/utils/microphone';
import { InputControlsContext } from './context';
import { useTimeout } from '@trinity/chat-components/hooks';

type Props = {
  children: React.ReactNode;
};

export const InputControlsContainer = (props: Props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(undefined);
  const soundLevelRef = useRef<HTMLDivElement>(undefined);
  const input = useContext(ChatSessionInputContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const stopTextToSpeech = useContext(StopTextToSpeechContext);
  const audioState = useContext(TextToSpeechStateContext);
  const voicemode = useContext(VoiceModeContext);
  const { canSubmit, onSubmit: handleSubmit } = useContext(SubmitMessageContext);
  const loadingVoiceClipAudioPlayer = useContext(LoadingVoiceClipContext);
  const chat = useContext(ActiveChatSessionContext);
  const [_micStateCtx, setMicStateCtx] = useContext(MicrophoneStateContext);

  const apiClient = useChatApiClient();

  const previousChatIdentifierRef = useRef<string>(chat?.identifier);
  const hasInitializedRef = useRef<boolean>(false);
  const userHasSpokenRef = useRef<boolean>(false);
  const wasTTSPlayingRef = useRef<boolean>(false);
  const suppressInputUntilRef = useRef<number>(0);

  const loadingVoiceClipAudioPlayerStart = useCallback(() => {
    apiClient.chat.fetchLoadingAudioClip().then(audioUrl => {
      loadingVoiceClipAudioPlayer.onPlay(audioUrl);
    });
  }, [apiClient.chat, loadingVoiceClipAudioPlayer]);

  const handleVoiceModeAutoSubmit = useCallback(() => {
    if (voicemode?.enabled && canSubmit) {
      loadingVoiceClipAudioPlayerStart();
      handleSubmit();
    }
  }, [voicemode?.enabled, canSubmit, loadingVoiceClipAudioPlayerStart, handleSubmit]);

  const [_, setTimer, clearTimer] = useTimeout(handleVoiceModeAutoSubmit, 200);

  const onSpeechFinished = useCallback((value: string, prefix: string) => {
    if (Date.now() < suppressInputUntilRef.current) return;

    const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
    let newVal = safePrefix ? `${safePrefix} ${capitalize(value)}` : capitalize(value);

    if (!endsWithPunctuation(newVal)) {
      newVal = newVal + '?';
    }

    dispatch({
      type: 'input-changed',
      payload: { value: newVal },
    });

    if (voicemode?.enabled) setTimer();
  }, [
    dispatch,
    setTimer,
    voicemode?.enabled,
  ]);

  const onAudioLevelChange = useCallback((level: number) => {
    if (!soundLevelRef.current) return;
    soundLevelRef.current?.setAttribute('style', `height: ${Math.min(20, Math.max(level, 4))}px`);
  }, []);

  const onInterimResult = useCallback((result: string, prefix: string) => {
    if (Date.now() < suppressInputUntilRef.current) return;

    userHasSpokenRef.current = true;

    const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
    const value = safePrefix ? `${safePrefix} ${capitalize(result)}` : capitalize(result);
    dispatch({
      type: 'input-changed',
      payload: { value },
    });
    clearTimer();
  }, [dispatch, clearTimer]);

  const { start, stop, listening, isSupported: speechRecogSupported, micState, errorMsg, interimResult } = useSpeechRecognition({
    continuous: true,
    timeout: voicemode.enabled ? 3000 : undefined,
    onResult: onSpeechFinished,
    onInterimResult,
    onAudioLevelChange,
    voicemode: voicemode.enabled,
    onVoiceModeAutoSubmit: handleVoiceModeAutoSubmit,
  });

  const onVoiceClick = useCallback(() => {
    if (listening) {
      clearTimer();
      stop();
    } else {
      requestMicrophonePermissionIfNeeded().then(granted => {
        if (granted) {
          const safeInput = input && typeof input === 'string' ? input : '';
          start(safeInput);
          textAreaRef.current.addEventListener('focus', () => {
            clearTimer();
            stop();
          });
        }
      });
    }
  }, [input, listening, start, stop, clearTimer]);

  const handleSubmitButtonClick = useCallback(() => {
    clearTimer();
    if (listening) {
      stop();
    }

    // Play loading clip when submitting in voice mode
    if (voicemode?.enabled) {
      loadingVoiceClipAudioPlayerStart();
    }

    handleSubmit();
  }, [handleSubmit, listening, stop, clearTimer, voicemode?.enabled, loadingVoiceClipAudioPlayerStart]);

  const handleVoiceModeClick = useCallback(() => {
    clearTimer();

    if (voicemode.enabled) {
      if (listening) {
        stop();
      }
      voicemode.disable();
      setMicStateCtx('inactive');
    }
    else {
      if (audioState?.state === 'playing' || audioState?.state === 'loading') {
        if (stopTextToSpeech) {
          stopTextToSpeech();
        }
        loadingVoiceClipAudioPlayer.onStop();
      }

      if (!listening) {
        voicemode.enable();

        userHasSpokenRef.current = false;

        const safeInput = input && typeof input === 'string' ? input : '';
        start(safeInput);

        textAreaRef.current.addEventListener('focus', () => {
          clearTimer();
          stop();
        });
      }
    }
  }, [
    audioState,
    input,
    clearTimer,
    listening,
    start,
    stop,
    voicemode,
    stopTextToSpeech,
    loadingVoiceClipAudioPlayer,
    setMicStateCtx,
  ]);

  const stopTtsDebounceRef = useRef<NodeJS.Timeout>(null);

  const shouldStopPlayback = useMemo(() => {
    const isPlaying = audioState?.state === 'playing' || audioState?.state === 'loading';
    return isPlaying && voicemode.enabled && listening && userHasSpokenRef.current;
  }, [
    audioState?.state,
    listening,
    voicemode.enabled,
  ]);

  useEffect(() => {
    if (shouldStopPlayback) {
      if (stopTtsDebounceRef.current) clearTimeout(stopTtsDebounceRef.current);
      stopTtsDebounceRef.current = setTimeout(() => {
        if (stopTextToSpeech) {
          stopTextToSpeech();
        }
        loadingVoiceClipAudioPlayer.onStop();
      }, 75);
    }
  }, [
    shouldStopPlayback,
    stopTextToSpeech,
    loadingVoiceClipAudioPlayer,
    audioState?.state,
    voicemode.enabled,
    listening,
  ]);

  useEffect(() => {
    const currentChatIdentifier = chat?.identifier;
    const previousChatIdentifier = previousChatIdentifierRef.current;

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousChatIdentifierRef.current = currentChatIdentifier;
      return;
    }

    if (previousChatIdentifier && currentChatIdentifier !== previousChatIdentifier) {

      suppressInputUntilRef.current = Date.now() + 1500;

      clearTimer();
      stop();
      try {
        SpeechRecognition.abortListening();
      } catch (error) {
        console.warn('SpeechRecognition.abortListening() not available:', error);
      }
      setMicStateCtx('inactive');

      if (voicemode?.enabled) voicemode.disable();

      if (stopTextToSpeech) {
        stopTextToSpeech(true);
      }

      setMicStateCtx('inactive');

      dispatch({ type: 'input-changed', payload: { value: '' } });
    }

    previousChatIdentifierRef.current = currentChatIdentifier;
  }, [chat?.identifier, listening, stop, clearTimer, voicemode, stopTextToSpeech, dispatch, setMicStateCtx]);

  useEffect(() => {
    if (audioState?.state === 'playing' || audioState?.state === 'loading') {
      wasTTSPlayingRef.current = true;
    }

    const ttsStopped = wasTTSPlayingRef.current &&
      (audioState?.state === 'stopped' || audioState?.state === 'paused' || audioState?.state === null);

    const shouldRestartMic = voicemode.enabled && ttsStopped && !listening && micState === 'inactive';

    if (shouldRestartMic) {
      wasTTSPlayingRef.current = false;
      userHasSpokenRef.current = false;
      const safeInput = input && typeof input === 'string' ? input : '';
      start(safeInput);
    } else if (audioState?.state === 'stopped' || audioState?.state === 'paused' || audioState?.state === null) {
      wasTTSPlayingRef.current = false;
    }
  }, [audioState?.state, voicemode.enabled, listening, micState, start, input]);

  const value = {
    onClickSubmit: handleSubmitButtonClick,
    onClickMicrophone: onVoiceClick,
    onClickVoiceMode: handleVoiceModeClick,
    speechRecognition: {
      errorMsg,
      interimResult,
      isSupported: speechRecogSupported,
      listening,
      micState,
      start,
      stop,
    },
    soundLevelRef,
    textAreaRef,
  };

  return (
    <InputControlsContext.Provider value={value}>
      {props.children}
    </InputControlsContext.Provider>
  );
};

InputControlsContainer.displayName = 'InputControls.Container';

function endsWithPunctuation(str: string) {
  const punctuation = /[.,!?;:'"-]/;
  return punctuation.test(str.charAt(str.length - 1));
}